import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'
import { isRegularSession } from './marketHours'

/**
 * Open reference line — a sibling of `previousDayHighLow.ts`. Draws each session day's
 * REGULAR-HOURS open (the open of the first bar at/after 09:30 ET) as a dashed horizontal
 * segment, painting from that 09:30 bar onward (NOT across pre-market). On the latest day
 * this is the current day's open. Self-contained: derives the level from the visible
 * `kLineDataList`, no daily datafeed. Colour comes from `extendData`.
 */
export interface OpenLineConfig {
  color: string
  timezone: string
}

const DAY_OPEN_NAME = 'DAY_OPEN'

// Cache DateTimeFormat per timezone (mirrors previousDayHighLow.ts).
const dtfCache = new Map<string, Intl.DateTimeFormat>()

function getDateStr(timestamp: number, tz: string): string {
  let dtf = dtfCache.get(tz)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dtfCache.set(tz, dtf)
  }
  return dtf.format(new Date(timestamp))
}

/** A day's open level plus the bar INDEX where the line starts painting (the 09:30 bar). */
export interface DayOpenSegment {
  value: number
  startIdx: number
}

/**
 * Each session day's REGULAR-HOURS open + the index of that 09:30 bar so the line paints from
 * 09:30 onward, not across pre-market. Falls back to a single daily bar (daily bars already
 * carry the RTH open); an intraday day with no regular-session bar (pre-market only) is skipped.
 */
export function buildDayOpenSegments(
  kLineDataList: KLineData[],
  tz: string,
): Map<string, DayOpenSegment> {
  const byDay = new Map<string, { bars: KLineData[]; indices: number[] }>()
  kLineDataList.forEach((bar, idx) => {
    if (!bar) return
    const d = getDateStr(bar.timestamp, tz)
    const g = byDay.get(d)
    if (g) {
      g.bars.push(bar)
      g.indices.push(idx)
    } else {
      byDay.set(d, { bars: [bar], indices: [idx] })
    }
  })

  const out = new Map<string, DayOpenSegment>()
  for (const [d, g] of byDay) {
    const pos = g.bars.findIndex((b) => isRegularSession(b.timestamp))
    if (pos >= 0) out.set(d, { value: g.bars[pos].open, startIdx: g.indices[pos] })
    else if (g.bars.length === 1) out.set(d, { value: g.bars[0].open, startIdx: g.indices[0] })
  }
  return out
}

export function drawDayOpen(params: IndicatorDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, xAxis, yAxis, indicator } = params
  const config = indicator.extendData as OpenLineConfig | undefined
  if (!config) return false

  const tz = config.timezone || 'America/New_York'
  const { from, to } = visibleRange
  if (from >= to || kLineDataList.length === 0) return false

  const segByDate = buildDayOpenSegments(kLineDataList, tz)
  if (segByDate.size === 0) return false

  ctx.save()
  ctx.strokeStyle = config.color
  ctx.lineWidth = 1
  ctx.setLineDash([4, 4])

  // Walk the visible range, flushing each day's segment at its boundary. The segment starts
  // at the day's RTH (09:30) bar index (clamped into view) and ends at the day's last bar.
  let currentDate = ''
  for (let i = from; i <= to; i++) {
    const bar = i < to ? kLineDataList[i] : null
    const d = bar ? getDateStr(bar.timestamp, tz) : ''

    if (d !== currentDate || i === to) {
      const seg = currentDate ? segByDate.get(currentDate) : undefined
      if (seg) {
        const start = Math.max(seg.startIdx, from)
        const end = i - 1
        if (start <= end) {
          const x1 = xAxis.convertToPixel(start)
          const x2 = xAxis.convertToPixel(end)
          const y = yAxis.convertToPixel(seg.value)
          if (isFinite(y) && isFinite(x1) && isFinite(x2)) {
            ctx.beginPath()
            ctx.moveTo(x1, y)
            ctx.lineTo(x2, y)
            ctx.stroke()
          }
        }
      }
      currentDate = d
    }
  }

  ctx.restore()
  return false
}

/** Register the day-open reference indicator with klinecharts (once). */
export const registerDayOpenIndicator = registerOnce(() => {
  registerIndicator({
    name: DAY_OPEN_NAME,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: drawDayOpen,
  })
})

export { DAY_OPEN_NAME }
